import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, Check, Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { useBooking } from "../../lib/BookingContext";

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function MiniMonth({ value, onChange, slotsByDate }) {
  // `value` is a Date for the currently-displayed month.
  const monthStart = new Date(value.getFullYear(), value.getMonth(), 1);
  const monthEnd = new Date(value.getFullYear(), value.getMonth() + 1, 0);
  // Build a 6x7 grid starting on Monday.
  const firstDayWeekIdx = (monthStart.getDay() + 6) % 7; // Mon=0..Sun=6
  const cells = [];
  for (let i = 0; i < firstDayWeekIdx; i++) cells.push(null);
  for (let d = 1; d <= monthEnd.getDate(); d++) cells.push(new Date(value.getFullYear(), value.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthLabel = value.toLocaleDateString("en-IE", { month: "short", year: "numeric" });

  const selectedDate = onChange.selectedDate;

  return (
    <div className="bg-[#0466ff] rounded-2xl p-4 text-white w-full sm:w-[280px] flex-shrink-0" data-testid="mini-month">
      <div className="flex items-center justify-between mb-3">
        <button
          className="p-1 rounded hover:bg-white/10"
          onClick={() => onChange.prev()}
          data-testid="month-prev"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="font-semibold" style={{ fontFamily: "Manrope" }}>{monthLabel}</div>
        <button
          className="p-1 rounded hover:bg-white/10"
          onClick={() => onChange.nextMonth()}
          data-testid="month-next"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-white/60 mb-1">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-sm">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          const k = ymd(c);
          const hasSlots = !!slotsByDate[k];
          const isPast = c < today;
          const isSelected = selectedDate && ymd(selectedDate) === k;
          return (
            <button
              key={i}
              onClick={() => hasSlots && onChange.pickDate(c)}
              disabled={!hasSlots || isPast}
              className={`aspect-square rounded-md transition-all text-center ${
                isSelected
                  ? "bg-white text-[#0466ff] font-bold"
                  : hasSlots
                  ? "hover:bg-white/15 font-semibold"
                  : "text-white/30 cursor-not-allowed"
              }`}
              data-testid={`cal-day-${k}`}
            >
              {c.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function StepTime() {
  const ctx = useBooking();
  const { service, location, slot, update, next, back, conflictMessage } = ctx;
  const [allSlots, setAllSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const [pickedDate, setPickedDate] = useState(slot ? new Date(slot) : null);
  const [pickerExpanded, setPickerExpanded] = useState(!slot);

  // Load 60 days of slots from today.
  useEffect(() => {
    if (!service || !location) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await api.slots({
        practiceId: location.id,
        serviceId: service.id,
        searchStart: ymd(new Date()),
      });
      if (cancelled) return;
      setAllSlots(data);
      setLoading(false);
      if (!pickedDate && data.length) {
        const first = new Date(data[0].slotStartTime);
        setPickedDate(first);
        setCalMonth(new Date(first.getFullYear(), first.getMonth(), 1));
      }
    })();
    return () => { cancelled = true; };
     
  }, [service, location]);

  const slotsByDate = useMemo(() => {
    const g = {};
    for (const s of allSlots) {
      const k = s.slotStartTime.slice(0, 10);
      (g[k] ||= []).push(s);
    }
    return g;
  }, [allSlots]);

  const slotsForPicked = pickedDate ? (slotsByDate[ymd(pickedDate)] || []) : [];

  const fmtTime = (iso) => {
    const d = new Date(iso);
    const h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    const hr12 = (h % 12 || 12).toString().padStart(2, "0");
    return `${hr12}:${m} ${ampm}`;
  };

  const fmtPickedHeader = (d) => d.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" });

  const { update: updateCtx } = ctx;
  const dismissConflict = () => updateCtx({ conflictMessage: null });

  const FooterButtons = ({ className = "" }) => (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <button onClick={back} className="ws-btn-primary inline-flex items-center gap-2" data-testid="time-back-button">
        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> BACK
      </button>
      <button
        onClick={next}
        className="ws-btn-primary inline-flex items-center gap-2"
        disabled={!slot}
        data-testid="time-next-button"
      >
        NEXT <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </div>
  );

  return (
    <div data-testid="step-time">
      {conflictMessage && (
        <div className="mb-5 rounded-lg border-2 border-[#D0021B] bg-[#FFF1F2] px-4 py-3 flex items-start justify-between gap-3" data-testid="conflict-banner">
          <p className="text-sm font-semibold text-[#D0021B] leading-snug">{conflictMessage}</p>
          <button onClick={dismissConflict} className="text-[#D0021B] hover:opacity-70 text-lg leading-none" aria-label="Dismiss">×</button>
        </div>
      )}
      <p className="text-[#5C5C5C] mb-6 leading-relaxed">
        Please select a date and available time slot for your{" "}
        <strong className="text-[#1A1A1A]">{service?.name}</strong>{" "}
        at <strong className="text-[#1A1A1A]">{location?.name}</strong>.
      </p>

      {loading ? (
        <>
          <div className="py-2" data-testid="loading-availability">
            <div className="text-sm font-semibold text-[#0466ff] mb-3" style={{ fontFamily: "Manrope" }}>
              Checking availability…
            </div>
            <div className="h-1.5 w-full rounded-full bg-[#E8F0FE] overflow-hidden relative">
              <div className="ws-loading-bar absolute top-0 h-full" />
            </div>
          </div>
          <FooterButtons className="mt-8 sm:mt-10" />
        </>
      ) : allSlots.length === 0 ? (
        <>
          <div className="ws-card p-6 text-center text-[#5C5C5C]" data-testid="no-slots-message">
            No availability in the next 90 days. Please call us on 01 667 8524.
          </div>
          <FooterButtons className="mt-8 sm:mt-10" />
        </>
      ) : slot && !pickerExpanded ? (
        <>
          <button
            type="button"
            onClick={() => setPickerExpanded(true)}
            className="w-full max-w-md rounded-lg bg-white border-2 border-[#0466ff] px-4 py-3 flex items-start gap-3 text-left hover:bg-[#F0F6FF] transition-colors"
            data-testid="selected-time-card"
          >
            <span className="w-5 h-5 mt-0.5 rounded-full bg-[#1FAD3A] flex-shrink-0 grid place-items-center">
              <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
            </span>
            <span className="flex-1 min-w-0 leading-tight">
              <span className="block text-sm text-[#1A1A1A] font-semibold">
                {new Date(slot).toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long" })}
              </span>
              <span className="block text-[12px] text-[#5C5C5C] mt-0.5">at {fmtTime(slot)}</span>
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0466ff] hover:underline self-center flex-shrink-0" data-testid="change-time-button">
              <Pencil className="w-3 h-3" strokeWidth={2.5} /> Change time
            </span>
          </button>
          <FooterButtons className="mt-6" />
        </>
      ) : (
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
          <MiniMonth
            value={calMonth}
            slotsByDate={slotsByDate}
            onChange={{
              selectedDate: pickedDate,
              prev: () => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1)),
              nextMonth: () => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1)),
              pickDate: (d) => setPickedDate(d),
            }}
          />

          <div className="flex-1 min-w-0 w-full">
            {pickedDate ? (
              <>
                <div className="bg-[#0466ff] text-white px-4 py-2 rounded-lg inline-block mb-3 font-semibold text-sm" data-testid="time-list-header">
                  {fmtPickedHeader(pickedDate)}
                </div>
                <div className="grid grid-flow-col grid-rows-[repeat(9,auto)] sm:grid-rows-[repeat(10,auto)] auto-cols-[160px] gap-x-3 gap-y-2 max-h-[480px] overflow-x-auto overflow-y-hidden pr-1">
                  {slotsForPicked.map((s) => {
                    const selected = slot === s.slotStartTime;
                    return (
                      <button
                        key={s.slotStartTime + s.clinicianId}
                        onClick={() => {
                          update({ slot: s.slotStartTime, slotClinicianId: s.clinicianId });
                          setPickerExpanded(false);
                        }}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-md border text-sm transition-all whitespace-nowrap ${
                          selected
                            ? "border-[#0466ff] bg-[#0466ff] text-white font-semibold"
                            : "border-[#E5E5E5] bg-white text-[#1A1A1A] hover:border-[#0466ff]"
                        }`}
                        data-testid={`time-slot-${s.slotStartTime}`}
                      >
                        <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 grid place-items-center ${selected ? "border-white" : "border-[#0466ff]/40"}`}>
                          {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </span>
                        <span>{fmtTime(s.slotStartTime)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-[#5C5C5C] text-sm">Pick a highlighted day from the calendar to see times.</div>
            )}
            <FooterButtons className="mt-6 sm:mt-8" />
          </div>
        </div>
      )}
    </div>
  );
}
